import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Box,
  IconButton,
  Drawer,
  Modal,
  Chip,
  Tooltip,
  Badge,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemText,
  Checkbox,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../Common/styles/table";
import {
  // setAiSearchQuery,
  setCurrentSearchId,
  setSearchParticipantsPage,
  patchRegistryFilters,
  clearRegistryFilterField,
  clearAllRegistryFilters,
} from "../../Redux/reducers/aiSearchSlice";
import { hasRegistryListFilters } from "../../utils/participantFilterActive";
import { hasRegistryDrawerFilters } from "../../utils/participantDrawerFilters";
import { useRegistryFilterOptions } from "../../hooks/useRegistryFilterOptions";
import { RegistryFilterContext } from "../../utils/registryColumnFilters";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchParticipantsList,
  fetchParticipantsListWithAI,
} from "../../Redux/actions/aiSearchAction";
import { useNavigate } from "react-router-dom";
import GlobalFilterModal from "./GlobalFilterModal";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { ModalBaseStyles, ModalHeader } from "../Common/styles/modal";
import { Add, KeyboardArrowDown } from "@mui/icons-material";
import ViewNoteDetailsModal from "../ParticipantRecords/ParticipantNotes/ViewNoteDetailsModal";
import ViewParticipantStudiesModal from "../ParticipantRecords/Modals/ViewParticipantStudiesModal";
import { DownloadIcon, UploadV2Icon } from "../Common/assets/Icons";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import http from "../../utils/http";
import AddParticipantModal from "./AddParticipantModal";
import RedactionHistoryModal from "./Modals/RedactionHistoryModal";
import BulkRedactConfirmModal from "./Modals/BulkRedactConfirmModal";
import BulkRedactPreviewModal from "./Modals/BulkRedactPreviewModal";
import { v4 as uuid } from "uuid";
import ImportParticipantsDrawer from "./ImportParticipantsDrawer";
import ScreenerImportDrawer from "./ScreenerImportDrawer";
import ScreenerImportHistoryDrawer from "./ScreenerImportHistoryDrawer";
import FreezeColumnsPopover from "../Common/UI/FreezeColumnsPopover";
import ParticipantTableLayout from "../Common/UI/ParticipantTableLayout";
import ParticipantListFooter from "../Common/UI/ParticipantListFooter";
import { useFrozenColumns } from "../../hooks/useFrozenColumns";
import { useStickyColumnOffsets } from "../../hooks/useStickyColumnOffsets";
import { orderColumnsForDisplay, getLastLeftPinnedColumnKey } from "../../utils/frozenColumns";
import {
  buildRegistryParticipantColumns,
  getRegistryColumnCellClassName,
  getRegistryColumnCellSx,
} from "./registryParticipantTableColumns";

const ParticipantList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showFilter, setShowFilter] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState<boolean>(false);
  const [selectedParticipantTags, setSelectedParticipantTags] = useState<
    string[]
  >([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [showNoteDetailsModal, setShowNoteDetailsModal] = useState(false);
  const [showStudyDetailsModal, setShowStudyDetailsModal] = useState(false);
  const [sampleCsvLoader, setSampleCsvLoader] = useState(false);
  const [exportLoader, setExportLoader] = useState(false);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [importAnchorEl, setImportAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<{
    participantId: string;
    status: "active" | "deactivated" | "redacted" | "deleted";
  } | null>(null);
  const [showImportDrawer, setShowImportDrawer] = useState(false);
  const [showScreenerImportDrawer, setShowScreenerImportDrawer] =
    useState(false);
  const [showScreenerImportHistory, setShowScreenerImportHistory] =
    useState(false);
  const [toggle, setToggle] = useState(true);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkActionAnchorEl, setBulkActionAnchorEl] =
    useState<null | HTMLElement>(null);
  const [showBulkRedactModal, setShowBulkRedactModal] = useState(false);
  const [showBulkReviveModal, setShowBulkReviveModal] = useState(false);
  const [showBulkDeactivateModal, setShowBulkDeactivateModal] = useState(false);
  const [showBulkReactivateModal, setShowBulkReactivateModal] = useState(false);
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  const tableRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<Record<string, HTMLTableCellElement | null>>({});

  const participantsLoader = useAppSelector(
    (state) => state.aiSearch.participantsLoader,
  );
  const searchParticipantsData = useAppSelector(
    (state) => state.aiSearch.searchParticipantsData,
  );
  const searchParticipantsCount = useAppSelector(
    (state) => state.aiSearch.searchParticipantsCount,
  );
  const unfilteredTotal = useAppSelector(
    (state) => state.aiSearch.unfilteredTotal,
  );
  const participantPage = useAppSelector(
    (state) => state.aiSearch.participantPage,
  );
  const currentSearchId = useAppSelector(
    (state) => state.aiSearch.currentSearchId,
  );
  const filters = useAppSelector((state) => state.aiSearch.filters);
  const {
    filterMeta,
    tagList,
    loading: filterOptionsLoading,
  } = useRegistryFilterOptions();

  const filterContext: RegistryFilterContext = useMemo(
    () => ({
      filters: filters || {},
      filterMeta,
      tagList,
      sortColumn,
      sortOrder,
      onSort: (column, order) => {
        setSortColumn(column);
        setSortOrder(order);
        dispatch(setSearchParticipantsPage(0));
      },
      onApply: (patch) => dispatch(patchRegistryFilters(patch)),
      onClearField: (field) => dispatch(clearRegistryFilterField(field)),
    }),
    [filters, filterMeta, tagList, sortColumn, sortOrder, dispatch],
  );

  const hasDrawerFilters = useMemo(
    () => hasRegistryDrawerFilters(filters),
    [filters],
  );
  const handleShowTags = (tags: string[]) => {
    setSelectedParticipantTags(tags);
    setShowTagsModal(true);
  };

  const closeTagsModal = () => {
    setShowTagsModal(false);
    setSelectedParticipantTags([]);
  };

  const handlePageChange = (_1: any, newPage: number) => {
    dispatch(setSearchParticipantsPage(newPage));
  };

  useEffect(() => {
    if (!currentSearchId) {
      dispatch(
        fetchParticipantsList(participantPage, filters, sortColumn, sortOrder),
      );
    } else {
      dispatch(fetchParticipantsListWithAI(currentSearchId, participantPage));
    }
  }, [
    dispatch,
    currentSearchId,
    participantPage,
    filters,
    toggle,
    sortColumn,
    sortOrder,
  ]);

  const handleClick = (row: any) => {
    navigate(
      `/app/participants/${row?.participantId}?participant=${
        row?.name || row?.email
      }`,
    );
  };

  const hasFilter = useMemo(() => {
    return Object.values(filters).some((value) => value !== "");
  }, [filters]);

  const hasActiveFilters = hasRegistryListFilters(filters, currentSearchId);

  const closeFilter = () => {
    setShowFilter(false);
  };

  const openFilter = () => {
    setShowFilter(true);
  };

  const handleShowNoteDetails = (row: any) => {
    setSelectedRow(row);
    setShowNoteDetailsModal(true);
  };

  const handleCloseNoteDetailsModal = () => {
    setShowNoteDetailsModal(false);
    setSelectedRow(null);
  };

  const handleShowStudyDetails = (row: any) => {
    setSelectedRow(row);
    setShowStudyDetailsModal(true);
  };

  const handleCloseStudyDetailsModal = () => {
    setShowStudyDetailsModal(false);
    setSelectedRow(null);
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportAnchorEl(null);
  };

  const handleImportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setImportAnchorEl(event.currentTarget);
  };

  const handleImportMenuClose = () => {
    setImportAnchorEl(null);
  };

  const handleExportParticipants = () => {
    exportParticipants();
  };

  const handleExportSampleCsv = () => {
    getSampleCsv();
  };
  const openAddParticipantModal = () => {
    setShowAddParticipantModal(true);
  };

  const closeAddParticipantModal = () => {
    setShowAddParticipantModal(false);
  };

  const refresh = useCallback(() => {
    setToggle((t) => !t);
  }, []);

  const refreshParticipantsList = useCallback(() => {
    refresh();
  }, [refresh]);

  const currentPageIds: string[] = (searchParticipantsData ?? []).map(
    (row: any) => row.participantId,
  );

  const isAllPageSelected =
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selectedIds.has(id));

  const handleSelectAllPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        currentPageIds.forEach((id) => next.add(id));
      } else {
        currentPageIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const handleToggleRow = (participantId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(participantId)) {
        next.delete(participantId);
      } else {
        next.add(participantId);
      }
      return next;
    });
  };

  // Single source of truth for bulk-action outcome messaging: exactly ONE
  // toast (success / partial / failure) per run. The confirm modals no longer
  // emit their own success toast, so these handlers own all feedback — this is
  // what prevents the contradictory "succeeded" + "failed" pair.
  const reportBulkOutcome = (
    verb: string,
    pastTense: string,
    succeeded: number,
    failures: string[],
  ) => {
    if (failures.length === 0) {
      toastMessage("success", `${succeeded} participant(s) ${pastTense}.`);
    } else if (succeeded === 0) {
      errorToastMessage(
        `No participants ${pastTense}. ${failures[0]}` as any,
      );
    } else {
      errorToastMessage(
        `${succeeded} participant(s) ${pastTense}, ${failures.length} could not be ${verb}. ${failures[0]}` as any,
      );
    }
  };

  const extractErr = (err: any): string =>
    err?.response?.data?.message || err?.message || "Unknown error";

  // Bulk deactivation. Deactivation only succeeds when the participant is
  // already redacted in every study (enforced by the BE guard). Looped
  // per-participant so one ineligible row does not abort the rest.
  const handleBulkDeactivateConfirm = async (reason: string) => {
    const ids = [...selectedIds];
    let succeeded = 0;
    const failures: string[] = [];
    for (const participantId of ids) {
      try {
        await http.post(`/admin/participants/${participantId}/deactivate`, {
          reason,
        });
        succeeded++;
      } catch (err: any) {
        failures.push(extractErr(err));
      }
    }
    reportBulkOutcome("deactivated", "deactivated", succeeded, failures);
    setSelectedIds(new Set());
    setToggle((t) => !t);
  };

  const handleBulkRedactConfirm = async (reason: string) => {
    const res = await http.post("/admin/participants/bulk-redact", {
      participantIds: [...selectedIds],
      reason,
    });
    const { succeeded, failed } = res.data.data as {
      succeeded: string[];
      failed: Array<{ id: string; error: string }>;
    };
    reportBulkOutcome(
      "redacted",
      "redacted",
      succeeded.length,
      failed.map((f) => f.error),
    );
    setSelectedIds(new Set());
    setToggle((t) => !t);
  };

  const handleBulkReviveConfirm = async (reason: string) => {
    const ids = [...selectedIds];
    let succeeded = 0;
    const failures: string[] = [];
    for (const participantId of ids) {
      try {
        await http.post(`/admin/participants/${participantId}/revive`, {
          reason,
        });
        succeeded++;
      } catch (err: any) {
        failures.push(extractErr(err));
      }
    }
    reportBulkOutcome("revived", "revived", succeeded, failures);
    setSelectedIds(new Set());
    setToggle((t) => !t);
  };

  // Bulk reactivation (deactivated → active). No PII is involved — this simply
  // reverses a deactivation. Offered alongside Redact for deactivated rows.
  const handleBulkReactivateConfirm = async (reason: string) => {
    const ids = [...selectedIds];
    let succeeded = 0;
    const failures: string[] = [];
    for (const participantId of ids) {
      try {
        await http.post(`/admin/participants/${participantId}/reactivate`, {
          reason,
        });
        succeeded++;
      } catch (err: any) {
        failures.push(extractErr(err));
      }
    }
    reportBulkOutcome("reactivated", "reactivated", succeeded, failures);
    setSelectedIds(new Set());
    setToggle((t) => !t);
  };

  // Derive the bulk action from the selected participants' redaction statuses.
  // The bulk menu mirrors the single-participant lifecycle: Active →
  // Deactivate, Deactivated → Redact, Redacted → Revive. A uniform selection
  // is required — a mixed selection offers no action, so the user cannot
  // accidentally skip a lifecycle step (e.g. jump Active straight to Redact).
  const selectedRows = (searchParticipantsData ?? []).filter((row: any) =>
    selectedIds.has(row.participantId),
  );
  const selectedStatuses = new Set<string>(
    selectedRows.map((row: any) => row.redactionStatus),
  );
  const isUniformSelection =
    selectedRows.length > 0 && selectedStatuses.size === 1;
  const uniformStatus = isUniformSelection
    ? selectedRows[0].redactionStatus
    : null;
  const allSelectedActive = uniformStatus === "active";
  const allSelectedDeactivated = uniformStatus === "deactivated";
  const allSelectedRedacted = uniformStatus === "redacted";

  const exportParticipants = async () => {
    try {
      setExportLoader(true);
      let httpUrl = `/admin/registry/participants/export?`;

      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          httpUrl += `&${key}=${value}`;
        }
      });
      if (sortColumn && sortOrder) {
        httpUrl += `&sortKey=${sortColumn}&sortOrder=${sortOrder}`;
      }

      const res = await http.get(httpUrl, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `platform-participants-${new Date().getTime()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      try {
        const text = await err.response.data.text();
        const errorData = JSON.parse(text);
        errorToastMessage(errorData || "Export failed");
      } catch (e) {
        errorToastMessage(err as Error);
      }
    } finally {
      setExportLoader(false);
    }
  };

  const getSampleCsv = async () => {
    try {
      setSampleCsvLoader(true);

      let res = await http.get("/data/participant-import-template", {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "sample.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toastMessage("success", res.data.message);
    } catch (err: any) {
      try {
        const text = await err.response.data.text();
        const errorData = JSON.parse(text);
        errorToastMessage(errorData || "Export failed");
      } catch (e) {
        errorToastMessage(err as Error);
      }
    } finally {
      setSampleCsvLoader(false);
    }
  };

  const columnDefinitions = useMemo(
    () =>
      buildRegistryParticipantColumns({
        currentPageIds,
        selectedIds,
        isAllPageSelected,
        handleSelectAllPage,
        handleToggleRow,
        handleClick,
        copiedCode,
        setCopiedCode,
        handleShowTags,
        handleShowNoteDetails,
        handleShowStudyDetails,
        setHistoryTarget,
        filterContext,
      }),
    [
      currentPageIds,
      selectedIds,
      isAllPageSelected,
      copiedCode,
      handleShowTags,
      handleShowNoteDetails,
      handleShowStudyDetails,
      filterContext,
    ],
  );

  const availableColumnKeys = useMemo(
    () =>
      columnDefinitions
        .filter((column) => column.freezable)
        .map((column) => column.key),
    [columnDefinitions],
  );

  const { frozenKeys, toggleFreeze, clearFrozen, isAtMax } = useFrozenColumns({
    listKey: "registry",
    availableColumnKeys,
  });

  const { displayColumns, frozenKeysInOrder } = useMemo(
    () => orderColumnsForDisplay(columnDefinitions, frozenKeys),
    [columnDefinitions, frozenKeys],
  );

  const lastLeftPinnedKey = useMemo(
    () => getLastLeftPinnedColumnKey(displayColumns, frozenKeys),
    [displayColumns, frozenKeys],
  );

  const stickyLeft = useStickyColumnOffsets(
    frozenKeysInOrder,
    headerRefs,
    tableRef,
    `${searchParticipantsData?.length ?? 0}-${frozenKeysInOrder.join(",")}`,
    "select",
  );

  const freezableColumns = useMemo(
    () =>
      columnDefinitions
        .filter((column) => column.freezable)
        .map((column) => ({ key: column.key, label: column.label })),
    [columnDefinitions],
  );

  useEffect(() => {
    const container = tableRef.current;

    const updateShadowClasses = () => {
      if (!container) return;

      const { scrollLeft, scrollWidth, clientWidth } = container;

      if (scrollLeft > 0) {
        if (!container.classList.contains("shadow-left")) {
          container.classList.add("shadow-left");
        }
      } else if (container.classList.contains("shadow-left")) {
        container.classList.remove("shadow-left");
      }

      if (scrollLeft + clientWidth < scrollWidth - 1) {
        if (!container.classList.contains("shadow-right")) {
          container.classList.add("shadow-right");
        }
      } else if (container.classList.contains("shadow-right")) {
        container.classList.remove("shadow-right");
      }
    };

    if (container) {
      updateShadowClasses();
      container.addEventListener("scroll", updateShadowClasses);
      container.addEventListener("scrollend", updateShadowClasses);
      window.addEventListener("resize", updateShadowClasses);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", updateShadowClasses);
        container.removeEventListener("scrollend", updateShadowClasses);
      }
      window.removeEventListener("resize", updateShadowClasses);
    };
  }, []);

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ParticipantTableLayout
        tableRef={tableRef}
        toolbar={
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            {(currentSearchId || hasFilter) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography fontWeight="medium" color="text.primary">
                  {currentSearchId ? "AI Search Results" : "Filtered Results"}
                </Typography>
                <Typography
                  sx={{ cursor: "pointer", color: "primary.main" }}
                  onClick={() => {
                    if (currentSearchId) {
                      dispatch(setCurrentSearchId(""));
                    } else {
                      dispatch(clearAllRegistryFilters());
                    }
                  }}
                >
                  Clear
                </Typography>
              </Box>
            )}
            <Box
              sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}
            >
              {!currentSearchId && (
                <>
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={
                        exportLoader || sampleCsvLoader ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                      endIcon={<KeyboardArrowDown />}
                      onClick={handleExportMenuOpen}
                      disabled={exportLoader || sampleCsvLoader}
                    >
                      Export
                    </Button>
                  </Box>
                  <Menu
                    anchorEl={exportAnchorEl}
                    open={Boolean(exportAnchorEl)}
                    onClose={handleExportMenuClose}
                    PaperProps={{
                      style: {
                        minWidth: "128px",
                      },
                    }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                  >
                    <MenuItem
                      onClick={handleExportSampleCsv}
                      disabled={sampleCsvLoader || exportLoader}
                    >
                      <ListItemText>Sample CSV</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={handleExportParticipants}
                      disabled={exportLoader || sampleCsvLoader}
                    >
                      <ListItemText>Participants</ListItemText>
                    </MenuItem>
                  </Menu>
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<UploadV2Icon />}
                      endIcon={<KeyboardArrowDown />}
                      onClick={handleImportMenuOpen}
                    >
                      Import
                    </Button>
                  </Box>
                  <Menu
                    anchorEl={importAnchorEl}
                    open={Boolean(importAnchorEl)}
                    onClose={handleImportMenuClose}
                    PaperProps={{
                      style: {
                        minWidth: "180px",
                      },
                    }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleImportMenuClose();
                        setShowImportDrawer(true);
                      }}
                    >
                      <ListItemText>Import with Preview</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleImportMenuClose();
                        setShowScreenerImportDrawer(true);
                      }}
                    >
                      <ListItemText>Mapped Import</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleImportMenuClose();
                        setShowScreenerImportHistory(true);
                      }}
                    >
                      <ListItemText>Import history</ListItemText>
                    </MenuItem>
                  </Menu>
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={openAddParticipantModal}
                    >
                      Add Participant
                    </Button>
                  </Box>
                </>
              )}
              {!currentSearchId && (
                <FreezeColumnsPopover
                  columns={freezableColumns}
                  frozenKeys={frozenKeys}
                  isAtMax={isAtMax}
                  onToggle={toggleFreeze}
                  onClear={clearFrozen}
                />
              )}
              {!currentSearchId && hasFilter && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => dispatch(clearAllRegistryFilters())}
                >
                  Clear filters
                </Button>
              )}
              {!currentSearchId && (
                <IconButton
                  onClick={openFilter}
                  sx={{
                    height: "48px",
                    width: "48px",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "8px",
                  }}
                >
                  <Badge
                    variant="dot"
                    color="primary"
                    overlap="circular"
                    invisible={!hasDrawerFilters}
                  >
                    <FilterAltIcon
                      color={hasDrawerFilters ? "primary" : "disabled"}
                      sx={{ fontSize: "24px" }}
                    />
                  </Badge>
                </IconButton>
              )}
              {selectedIds.size > 0 && (
                <>
                  <Button
                    variant="contained"
                    color="warning"
                    endIcon={<KeyboardArrowDown />}
                    onClick={(e) => setBulkActionAnchorEl(e.currentTarget)}
                  >
                    Actions ({selectedIds.size})
                  </Button>
                  <Menu
                    anchorEl={bulkActionAnchorEl}
                    open={Boolean(bulkActionAnchorEl)}
                    onClose={() => setBulkActionAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                  >
                    {!isUniformSelection ? (
                      <MenuItem disabled>
                        <ListItemText>
                          Mixed selection — select participants in the same state
                          (Active, Deactivated, or Redacted)
                        </ListItemText>
                      </MenuItem>
                    ) : allSelectedActive ? (
                      <MenuItem
                        onClick={() => {
                          setBulkActionAnchorEl(null);
                          setShowBulkDeactivateModal(true);
                        }}
                      >
                        <ListItemText>Deactivate Selected</ListItemText>
                      </MenuItem>
                    ) : allSelectedDeactivated ? (
                      <>
                        <MenuItem
                          onClick={() => {
                            setBulkActionAnchorEl(null);
                            setShowBulkReactivateModal(true);
                          }}
                        >
                          <ListItemText>Reactivate Selected</ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setBulkActionAnchorEl(null);
                            setShowBulkRedactModal(true);
                          }}
                        >
                          <ListItemText>Redact Selected</ListItemText>
                        </MenuItem>
                      </>
                    ) : allSelectedRedacted ? (
                      <MenuItem
                        onClick={() => {
                          setBulkActionAnchorEl(null);
                          setShowBulkReviveModal(true);
                        }}
                      >
                        <ListItemText>Revive Selected</ListItemText>
                      </MenuItem>
                    ) : (
                      <MenuItem disabled>
                        <ListItemText>No bulk action available</ListItemText>
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>
          </Box>
        }
        footer={
          <ParticipantListFooter
            filteredCount={searchParticipantsCount}
            totalCount={unfilteredTotal}
            hasActiveFilters={hasActiveFilters}
            page={participantPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            loading={participantsLoader}
          />
        }
      >
        <Table
          sx={TableBorderRadiusTopLeftRight}
          stickyHeader
          className="scrollable-table"
        >
          <TableHead>
            <TableRow>
              {displayColumns.map((column) => (
                <StyledTableCell
                  key={column.key}
                  ref={(element) => {
                    headerRefs.current[column.key] =
                      element as HTMLTableCellElement | null;
                  }}
                  padding={column.padding}
                  sx={getRegistryColumnCellSx(
                    column,
                    frozenKeys,
                    frozenKeysInOrder,
                    stickyLeft,
                    true,
                  )}
                  className={getRegistryColumnCellClassName(
                    column,
                    frozenKeys,
                    lastLeftPinnedKey,
                  )}
                >
                  {column.renderHeader()}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          {!participantsLoader && searchParticipantsCount > 0 && (
            <TableBody>
              {searchParticipantsData?.map((row: any) => (
                <TableRow key={row?.id}>
                  {displayColumns.map((column) => (
                    <StyledTableCell
                      key={column.key}
                      padding={column.padding}
                      sx={{
                        ...getRegistryColumnCellSx(
                          column,
                          frozenKeys,
                          frozenKeysInOrder,
                          stickyLeft,
                          false,
                        ),
                        ...(column.key === "email"
                          ? { cursor: "pointer" }
                          : {}),
                        ...(column.key === "status" ||
                        column.key === "redactionStatus"
                          ? { textTransform: "capitalize" }
                          : {}),
                      }}
                      className={getRegistryColumnCellClassName(
                        column,
                        frozenKeys,
                        lastLeftPinnedKey,
                      )}
                      onClick={
                        column.key === "email"
                          ? () => handleClick(row)
                          : column.key === "select"
                            ? (e) => e.stopPropagation()
                            : column.key === "systemId"
                              ? (e) => e.stopPropagation()
                              : undefined
                      }
                    >
                      {column.renderCell(row)}
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {!participantsLoader && searchParticipantsCount === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {participantsLoader && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </ParticipantTableLayout>
        {showFilter && (
          <Drawer open={showFilter} onClose={closeFilter} anchor="right">
            <GlobalFilterModal
              closeModal={closeFilter}
              filterMeta={filterMeta}
              tagList={tagList}
              loading={filterOptionsLoading}
            />
          </Drawer>
        )}
        {showTagsModal && (
          <Modal open={showTagsModal} onClose={closeTagsModal}>
            <Box sx={{ ...ModalBaseStyles, minHeight: "10vh" }}>
              <ModalHeader title={"Tags"} onCloseClick={closeTagsModal} />
              <Box>
                {selectedParticipantTags?.map((tag, index) => (
                  <Chip
                    sx={{
                      backgroundColor: "#F6F1F8",
                      paddingX: "10px",
                      paddingY: "2px",
                      borderRadius: "6px",
                      fontSize: 10,
                      fontWeight: 500,
                      color: "primary.main",
                      m: 0.5,
                    }}
                    key={index}
                    label={tag}
                  />
                ))}
              </Box>
            </Box>
          </Modal>
        )}
        {showNoteDetailsModal && selectedRow && (
          <ViewNoteDetailsModal
            open={showNoteDetailsModal}
            onClose={handleCloseNoteDetailsModal}
            data={selectedRow}
          />
        )}
        {showStudyDetailsModal && selectedRow && (
          <ViewParticipantStudiesModal
            open={showStudyDetailsModal}
            onClose={handleCloseStudyDetailsModal}
            data={selectedRow}
          />
        )}
        {showAddParticipantModal && (
          <AddParticipantModal
            showModal={showAddParticipantModal}
            closeModal={closeAddParticipantModal}
            refreshPage={refreshParticipantsList}
          />
        )}
        {historyTarget && (
          <RedactionHistoryModal
            open={!!historyTarget}
            onClose={() => setHistoryTarget(null)}
            participantId={historyTarget.participantId}
            currentStatus={historyTarget.status}
          />
        )}
        {showImportDrawer && (
          <ImportParticipantsDrawer
            open={showImportDrawer}
            onClose={() => setShowImportDrawer(false)}
            onImportComplete={refreshParticipantsList}
          />
        )}
        {showScreenerImportDrawer && (
          <ScreenerImportDrawer
            open={showScreenerImportDrawer}
            onClose={() => setShowScreenerImportDrawer(false)}
            onImportComplete={refreshParticipantsList}
          />
        )}
        {showScreenerImportHistory && (
          <ScreenerImportHistoryDrawer
            open={showScreenerImportHistory}
            onClose={() => setShowScreenerImportHistory(false)}
            onRollbackComplete={refreshParticipantsList}
          />
        )}
        <BulkRedactConfirmModal
          open={showBulkDeactivateModal}
          onClose={() => setShowBulkDeactivateModal(false)}
          count={selectedIds.size}
          onConfirm={handleBulkDeactivateConfirm}
          mode="deactivate"
        />
        <BulkRedactConfirmModal
          open={showBulkReactivateModal}
          onClose={() => setShowBulkReactivateModal(false)}
          count={selectedIds.size}
          onConfirm={handleBulkReactivateConfirm}
          mode="reactivate"
        />
        {showBulkRedactModal && (
          <BulkRedactPreviewModal
            open={showBulkRedactModal}
            onClose={() => setShowBulkRedactModal(false)}
            participantIds={[...selectedIds]}
            onConfirm={handleBulkRedactConfirm}
          />
        )}
        <BulkRedactConfirmModal
          open={showBulkReviveModal}
          onClose={() => setShowBulkReviveModal(false)}
          count={selectedIds.size}
          onConfirm={handleBulkReviveConfirm}
          mode="revive"
        />
    </Box>
  );
};

export default ParticipantList;
